import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { LifeBuoy } from 'lucide-react';

/**
 * Iter-190 — Standby / Floater Mode controls for Create/Edit Shift forms
 * (admin + client).
 *
 * IMPORTANT POLICY (Iter-190 follow-up): standby is **on-site only** —
 * the talent must remain at the venue and be ready to step in. Copy
 * surfaces here reflect that. The talent-facing description field will
 * be appended with the canonical onsite-standby line at apply time.
 *
 * Props:
 *   form    : shift form state object
 *   update  : (key, value) => void
 */
const StandbyControls = ({ form, update }) => {
  const enabled = !!form.standby_enabled;

  return (
    <div
      className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50/40 p-4 sm:p-5 space-y-4"
      data-testid="standby-controls"
    >
      {/* Header row — matches Backup Staffing card visually. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Label className="flex items-center gap-2 font-semibold text-gray-900">
            <LifeBuoy className="w-4 h-4 text-amber-600 shrink-0" />
            Standby / Floater Mode
          </Label>
          <p className="text-xs text-gray-600 mt-1 leading-snug">
            Onsite emergency-replacement bench. Standby talent must remain at the venue
            and be ready to step in if a primary worker no-shows, cancels, or leaves early.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => update('standby_enabled', v)}
          data-testid="standby-enabled-toggle"
          className="mt-1 shrink-0"
        />
      </div>

      {enabled && (
        <div className="space-y-4 pt-2 border-t border-amber-200">
          {/* Roster size row. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Standby roster size</Label>
              <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 h-10">
                <span className="text-xs text-gray-700">Unlimited</span>
                <Switch
                  checked={!!form.standby_unlimited}
                  onCheckedChange={(v) => update('standby_unlimited', v)}
                  data-testid="standby-unlimited-toggle"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Limit (max standby talent)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                disabled={!!form.standby_unlimited}
                value={form.standby_limit || ''}
                onChange={(e) => update('standby_limit', e.target.value)}
                placeholder={form.standby_unlimited ? 'Unlimited' : 'e.g. 10'}
                className="h-10"
                data-testid="standby-limit-input"
              />
            </div>
          </div>

          {/* Pay row — equal columns, matching heights. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Onsite Standby Pay ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.standby_pay_amount || ''}
                onChange={(e) => update('standby_pay_amount', e.target.value)}
                placeholder="e.g. 50.00"
                className="h-10"
                data-testid="standby-pay-amount-input"
              />
              <p className="text-[10px] leading-tight text-gray-500">
                Flat compensation for remaining onsite, even if not activated into the shift.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Hourly Rate If Activated ($/hr)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.standby_hourly_rate || ''}
                onChange={(e) => update('standby_hourly_rate', e.target.value)}
                placeholder="e.g. 25.00"
                className="h-10"
                data-testid="standby-hourly-rate-input"
              />
              <p className="text-[10px] leading-tight text-gray-500">
                Standard hourly rate that kicks in the moment standby is activated.
              </p>
            </div>
          </div>

          {/* Description / notes row — equal heights. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Talent-facing description</Label>
              <Textarea
                rows={3}
                maxLength={500}
                value={form.standby_description || ''}
                onChange={(e) => update('standby_description', e.target.value)}
                placeholder="e.g. Check in onsite at call time, then remain at the venue lounge until released."
                className="min-h-[88px]"
                data-testid="standby-description-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Internal notes (admin only)</Label>
              <Textarea
                rows={3}
                maxLength={500}
                value={form.standby_notes || ''}
                onChange={(e) => update('standby_notes', e.target.value)}
                placeholder="e.g. Activate from standby only after backup roster exhausted."
                className="min-h-[88px]"
                data-testid="standby-notes-input"
              />
            </div>
          </div>

          {/* Auto-promote row. */}
          <div className="flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-white px-3 py-2.5">
            <div className="flex-1 min-w-0">
              <Label className="text-xs font-medium text-gray-800">
                Auto-promote first standby applicant
              </Label>
              <p className="text-[10px] leading-tight text-gray-500 mt-0.5">
                When a confirmed worker cancels, instantly activate the next standby talent into the shift.
              </p>
            </div>
            <Switch
              checked={form.standby_auto_promote !== false}
              onCheckedChange={(v) => update('standby_auto_promote', v)}
              data-testid="standby-auto-promote-toggle"
              className="mt-1 shrink-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StandbyControls;
